<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'

// 定义组件属性
const props = defineProps({
  // 视频源URL
  src: {
    type: String,
    required: true
  },
  // 是否自动播放
  autoplay: {
    type: Boolean,
    default: false
  },
  // 是否循环播放
  loop: {
    type: Boolean,
    default: false
  },
  // 是否静音
  muted: {
    type: Boolean,
    default: false
  },
  // 初始音量 (0-1)
  volume: {
    type: Number,
    default: 0.8,
    validator: (value) => value >= 0 && value <= 1
  },
  // 播放器宽度
  width: {
    type: String,
    default: '80%'
  },
  // 播放器高度
  height: {
    type: String,
    default: 'auto'
  }
})

// 定义事件
const emit = defineEmits(['play', 'pause', 'ended', 'timeupdate', 'volumechange', 'error'])

// 响应式数据
const videoRef = ref(null)
const isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(0)
const currentVolume = ref(props.volume)
const isMuted = ref(props.muted)
const isFullScreen = ref(false)
const showControls = ref(false)
const controlsTimeout = ref(null)

// 格式化时间为 00:00 格式
const formatTime = (seconds) => {
  if (isNaN(seconds)) return '00:00'
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
}

// 播放/暂停切换
const togglePlay = () => {
  if (!videoRef.value) return
  
  if (isPlaying.value) {
    videoRef.value.pause()
  } else {
    videoRef.value.play().catch(err => {
      console.error('播放失败:', err)
      emit('error', err)
    })
  }
}

// 更新进度条
const updateProgress = (event) => {
  if (!videoRef.value) return
  
  const progressBar = event.target
  const rect = progressBar.getBoundingClientRect()
  const pos = (event.clientX - rect.left) / rect.width
  const newTime = pos * duration.value
  
  videoRef.value.currentTime = newTime
  currentTime.value = newTime
}

// 处理进度条拖动开始
const handleProgressMouseDown = (event) => {
  updateProgress(event)
  videoRef.value.pause()
  document.addEventListener('mousemove', updateProgress)
  document.addEventListener('mouseup', handleProgressMouseUp)
}

// 处理进度条拖动结束
const handleProgressMouseUp = () => {
  document.removeEventListener('mousemove', updateProgress)
  document.removeEventListener('mouseup', handleProgressMouseUp)
  if (isPlaying.value) {
    videoRef.value.play()
  }
}

// 切换静音
const toggleMute = () => {
  if (!videoRef.value) return
  
  isMuted.value = !isMuted.value
  videoRef.value.muted = isMuted.value
  emit('volumechange', { volume: currentVolume.value, muted: isMuted.value })
}

// 更新音量
const updateVolume = (event) => {
  if (!videoRef.value) return
  
  const volumeSlider = event.target
  const volume = parseFloat(volumeSlider.value)
  currentVolume.value = volume
  videoRef.value.volume = volume
  
  // 如果音量不为0，取消静音
  if (volume > 0 && isMuted.value) {
    isMuted.value = false
    videoRef.value.muted = false
  }
  
  emit('volumechange', { volume: volume, muted: isMuted.value })
}

// 切换全屏
const toggleFullScreen = () => {
  if (!videoRef.value) return
  
  const container = videoRef.value.parentElement
  
  if (!document.fullscreenElement) {
    // 进入全屏
    if (container.requestFullscreen) {
      container.requestFullscreen()
    } else if (container.webkitRequestFullscreen) {
      container.webkitRequestFullscreen()
    } else if (container.msRequestFullscreen) {
      container.msRequestFullscreen()
    }
    isFullScreen.value = true
  } else {
    // 退出全屏
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen()
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen()
    }
    isFullScreen.value = false
  }
}

// 关闭播放器
const handleClose = () => {
  emit('close')
}

// 显示控制栏并设置自动隐藏
const showPlayerControls = () => {
  showControls.value = true
  
  // 清除之前的定时器
  if (controlsTimeout.value) {
    clearTimeout(controlsTimeout.value)
  }
  
  // 5秒后自动隐藏控制栏（仅在播放状态下）
  if (isPlaying.value) {
    controlsTimeout.value = setTimeout(() => {
      showControls.value = false
    }, 5000)
  }
}

// 监听全屏变化
const handleFullScreenChange = () => {
  isFullScreen.value = !!document.fullscreenElement
}

// 视频事件处理
const handlePlay = () => {
  isPlaying.value = true
  emit('play')
}

const handlePause = () => {
  isPlaying.value = false
  emit('pause')
}

const handleTimeUpdate = () => {
  if (!videoRef.value) return
  currentTime.value = videoRef.value.currentTime
  emit('timeupdate', { currentTime: currentTime.value, duration: duration.value })
}

const handleDurationChange = () => {
  if (!videoRef.value) return
  duration.value = videoRef.value.duration
}

const handleEnded = () => {
  isPlaying.value = false
  emit('ended')
}

const handleError = (event) => {
  emit('error', event)
}

// 监听属性变化
watch(() => props.src, (newSrc) => {
  if (videoRef.value) {
    videoRef.value.load()
  }
})

watch(() => props.autoplay, (newAutoplay) => {
  if (videoRef.value && newAutoplay && !isPlaying.value) {
    videoRef.value.play()
  }
})

watch(() => props.loop, (newLoop) => {
  if (videoRef.value) {
    videoRef.value.loop = newLoop
  }
})

watch(() => props.muted, (newMuted) => {
  if (videoRef.value) {
    isMuted.value = newMuted
    videoRef.value.muted = newMuted
  }
})

watch(() => props.volume, (newVolume) => {
  if (videoRef.value) {
    currentVolume.value = newVolume
    videoRef.value.volume = newVolume
  }
})

// 组件挂载
onMounted(() => {
  if (videoRef.value) {
    // 设置初始属性
    videoRef.value.loop = props.loop
    videoRef.value.muted = props.muted
    videoRef.value.volume = props.volume
    
    // 添加全屏监听
    document.addEventListener('fullscreenchange', handleFullScreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange)
    document.addEventListener('MSFullscreenChange', handleFullScreenChange)
  }
})

// 组件卸载
onUnmounted(() => {
  if (controlsTimeout.value) {
    clearTimeout(controlsTimeout.value)
  }
  
  document.removeEventListener('fullscreenchange', handleFullScreenChange)
  document.removeEventListener('webkitfullscreenchange', handleFullScreenChange)
  document.removeEventListener('MSFullscreenChange', handleFullScreenChange)
})
</script>

<template>
  <Teleport to="body">
    <div 
      class="video-player-container" 
      :style="{ width: width, height: height }"
      @mouseenter="showPlayerControls"
      @mouseleave="showControls = false"
    >
      <video
        ref="videoRef"
        class="video-player"
        :src="src"
        :autoplay="autoplay"
        controls
        @play="handlePlay"
        @pause="handlePause"
        @timeupdate="handleTimeUpdate"
        @durationchange="handleDurationChange"
        @ended="handleEnded"
        @error="handleError"
        @click="togglePlay"
      >
        您的浏览器不支持HTML5视频播放
      </video>
      
      <!-- 关闭按钮 -->
      <button class="close-button" @click.stop="handleClose" title="关闭播放器">
       <el-icon><CloseBold /></el-icon>
      </button>
    </div>
  </Teleport>
</template>

<style scoped>
.video-player-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #000;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10000;
  user-select: none;
}
.video-player {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  transition: all 0.3s ease;
}

.close-button:hover {
    background-color: rgba(255, 0, 0, 0.7);
    transform: scale(1.1);
  }

/* 控制栏样式 */
.video-controls {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  opacity: 0;
  transition: opacity 0.3s ease;
  background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 30%);
}

.video-controls.show {
  opacity: 1;
}

.controls-top {
  flex: 1;
  padding: 10px;
  display: flex;
  align-items: flex-start;
}

.video-title {
  color: #fff;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  text-shadow: 0 1px 2px rgba(0,0,0,0.8);
}

.controls-bottom {
  padding: 10px;
  background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%);
}

/* 进度条样式 */
.progress-container {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.progress-bar {
  flex: 1;
  height: 4px;
  background-color: rgba(255,255,255,0.3);
  border-radius: 2px;
  cursor: pointer;
  position: relative;
  margin-right: 10px;
}

.progress-bar:hover {
  height: 6px;
}

.progress-filled {
  height: 100%;
  background-color: #2196f3;
  border-radius: 2px;
  position: relative;
}

.progress-filled::after {
  content: '';
  position: absolute;
  right: -5px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: #2196f3;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.progress-bar:hover .progress-filled::after {
  opacity: 1;
}

.time-display {
  color: #fff;
  font-size: 12px;
  white-space: nowrap;
  min-width: 80px;
  text-align: right;
}

.time-separator {
  margin: 0 3px;
}

/* 控制按钮样式 */
.control-buttons {
  display: flex;
  align-items: center;
}

.control-btn {
  background: transparent;
  border: none;
  color: #fff;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  margin-right: 5px;
}

.control-btn:hover {
  background-color: rgba(255,255,255,0.2);
}

.control-btn:active {
  transform: scale(0.95);
}

/* 音量控制样式 */
.volume-control {
  display: flex;
  align-items: center;
  margin-right: 10px;
}

.volume-slider {
  width: 80px;
  margin-left: 5px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.volume-slider:hover {
  opacity: 1;
}

/* 中央大播放按钮 */
.big-play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0,0,0,0.6);
  border: none;
  border-radius: 50%;
  color: #fff;
  width: 80px;
  height: 80px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  opacity: 0.8;
}

.big-play-button:hover {
  background: rgba(0,0,0,0.8);
  transform: translate(-50%, -50%) scale(1.1);
  opacity: 1;
}

.big-play-button:active {
  transform: translate(-50%, -50%) scale(0.95);
}

/* 响应式调整 */
@media (max-width: 768px) {
  .volume-slider {
    width: 60px;
  }
  
  .time-display {
    min-width: 70px;
    font-size: 11px;
  }
  
  .big-play-button {
    width: 60px;
    height: 60px;
  }
}
</style>